import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-regular-svg-icons";

import { UserAuth } from "../../../authentication/AuthContext.jsx";
import { auth } from "../../../authentication/Firebase.jsx";
import { requiresVerification } from "../../../authentication/emailVerification.js";
import { PrimaryButton, SecondaryButton } from "../../../components/button/Button.jsx";
import EmailIcon from "../../../assets/common/email.svg?react";

import "./SignupLogin.css";

const RESEND_COOLDOWN_SECONDS = 60;

// Where the email gate sends a signed-in-but-unverified session.
const VerifyEmailRequired = () => {
    const { user, sendVerificationEmail, logOut } = UserAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [isResending, setIsResending] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    const from = location.state?.from;
    const returnPath = from && from !== location.pathname ? from : '/home';

    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleResend = async () => {
        setIsResending(true);
        setError("");
        setNotice("");
        // On the tap, not on the response: the countdown replaces the button, so starting it
        // after the await leaves the resend control live for the length of the send. Rolled
        // back in the catch so a failure can be retried immediately.
        setResendTimer(RESEND_COOLDOWN_SECONDS);
        try {
            // sendEmailVerification needs the live Firebase user, not the context snapshot.
            await sendVerificationEmail(auth.currentUser);
            setNotice("Verification link sent. Please check your inbox.");
        } catch (err) {
            setResendTimer(0);
            setError(err?.code === 'auth/too-many-requests'
                ? "Too many requests. Please wait a moment before requesting another link."
                : "We couldn't send the verification link. Please try again in a moment.");
        } finally {
            setIsResending(false);
        }
    };

    const handleSignOut = async () => {
        await logOut();
        navigate('/');
    };

    if (!user) {
        return <Navigate to="/account?mode=login" replace />;
    }

    if (!requiresVerification(user)) {
        return <Navigate to={returnPath} replace />;
    }

    return (
        <div className="login-signup-page">
            <div className="login-signup-form-container">
                <div className="login-signup-form-icon-container">
                    <EmailIcon />
                </div>
                <h2 className="login-signup-form-title">Verify Your Email</h2>
                <p className="login-signup-form-helptext">
                    Your email address hasn't been verified yet. Open the verification link we sent to:
                </p>
                <div className="login-signup-form-email-badge">{user.email || ''}</div>
                {error && <div className="error-message">{error}</div>}
                {notice && <p className="login-signup-form-helptext">{notice}</p>}
                {resendTimer > 0
                    ? <div className="login-signup-form-retry">
                        <FontAwesomeIcon icon={faClock} />
                        Try again in <span>{resendTimer}s</span>
                    </div>
                    : <PrimaryButton onClick={handleResend} disabled={isResending} isEnterTriggered isFullWidth>
                        {isResending ? 'Sending...' : 'Resend Verification Link'}
                    </PrimaryButton>}
                <SecondaryButton onClick={handleSignOut} isFullWidth>Back To Home Page</SecondaryButton>
                <p className="login-signup-form-helptext">
                    Please check your inbox (and spam folder). After verifying, log in again to access
                    the dashboard.
                </p>
            </div>
        </div>
    );
};

export default VerifyEmailRequired;
